import { useCallback, useEffect, useState } from "react";
import { getTagGroupsForAssetTenant } from "@/api/tags-api.js";

/**
 * Manage asset-scoped tag groups and selected group state
 * @param {string|number|null|undefined} assetId - Asset ID
 * @returns {Object} Group data and selection state
 */
export function useAssetTagGroups(assetId) {
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  /**
   * Fetch tag groups for current asset tenant
   * @returns {Promise<void>} Async completion
   */
  const refetchGroups = useCallback(async () => {
    if (!assetId) {
      setGroups([]);
      setSelectedGroupId(null);
      setIsLoadingGroups(false);
      return;
    }

    setIsLoadingGroups(true);
    try {
      const response = await getTagGroupsForAssetTenant(assetId);
      setGroups(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching tag groups:", error);
      setGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  }, [assetId]);

  useEffect(() => {
    refetchGroups();
  }, [refetchGroups]);

  useEffect(() => {
    if (!assetId || isLoadingGroups) {
      return;
    }

    const isValidSelection =
      selectedGroupId !== null
      && groups.some(
        (group) => Number(group.TagGroupId) === Number(selectedGroupId)
      );

    if (selectedGroupId !== null && !isValidSelection) {
      setSelectedGroupId(null);
    }
  }, [assetId, groups, isLoadingGroups, selectedGroupId]);

  return {
    groups,
    isLoadingGroups,
    selectedGroupId,
    setSelectedGroupId,
    refetchGroups,
  };
}
